import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  CONFIG_PADRAO, ehAssinante, limiteDeDesafios, tetoDeCaloria,
  type AppConfig, type Plano,
} from "@nhomi/core";
import { restaurarCompras } from "@/data/assinatura";
import { watchAppConfig } from "@/data/config";
import { watchUser, type Perfil } from "@/data/users";
import { nomeDaSessao, useSession } from "./auth";

/* ============================================================
   O plano de quem está usando, e os limites que vêm com ele

   Um contexto, como os bloqueios: a tela de publicar, a de caloria e a
   de conta perguntam a mesma coisa, e três assinaturas do mesmo
   documento seriam três leituras para a mesma resposta.

   O que sai daqui é para MOSTRAR, nunca para permitir. Quem recusa a
   estimativa acima do teto é a função; quem recusa a escrita do campo
   `plano` é a regra do Firestore. A tela só evita pedir o que vai ser
   negado — e explicar antes é melhor que errar depois.
   ============================================================ */

interface Estado {
  config: AppConfig;
  plano: Plano;
  assinante: boolean;
  tetoDeCaloriaHoje: number;
  maxDeDesafios: number;
  /** O documento `users/{uid}` inteiro — o nome e a foto que valem. */
  perfil: Perfil | null;
}

const PADRAO: Estado = {
  config: CONFIG_PADRAO,
  plano: "gratis",
  assinante: false,
  tetoDeCaloriaHoje: CONFIG_PADRAO.tetoCaloriaGratis,
  maxDeDesafios: CONFIG_PADRAO.desafiosGratis,
  perfil: null,
};

const Contexto = createContext<Estado>(PADRAO);

export function PlanoProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const [config, setConfig] = useState<AppConfig>(CONFIG_PADRAO);
  const [plano, setPlano] = useState<Plano>("gratis");
  const [perfil, setPerfil] = useState<Perfil | null>(null);

  /* SÓ COM SESSÃO, e isso é a correção de um bug que foi pro ar.

     A regra do Firestore exige estar logado pra ler `config/app`.
     Montado na abertura, sem `uid` nas dependências, o ouvinte ia sem
     token, levava `permission-denied` e MORRIA — e como ele não volta
     sozinho quando o login chega, o app passava a sessão inteira com o
     padrão do core. O painel dizia 2 estimativas por dia, o servidor
     recusava na terceira e a tela prometia 5.

     `loading` também conta: enquanto a sessão está sendo restaurada,
     `user` é `null` e montar agora seria montar cedo de novo. */
  const uid = user?.uid;
  useEffect(() => {
    if (!uid) return;
    return watchAppConfig(setConfig);
  }, [uid]);

  /* O mesmo `watchUser` responde duas perguntas: o plano e o perfil —
     nome e foto que valem. As telas que desenham "eu" (o avatar do
     canto, o do campo de comentário) leem daqui, porque a sessão guarda
     os dados do LOGIN e a troca em Conta não os atualiza. */
  useEffect(() => {
    if (!uid) { setPlano("gratis"); setPerfil(null); return; }
    return watchUser(uid, (p) => {
      setPerfil(p);
      setPlano(ehAssinante(p?.plano) ? "assinante" : "gratis");
    });
  }, [uid]);

  /* Reconcilia com a loja uma vez por sessão, em silêncio.
     
     Fica aqui, junto de quem lê o plano, porque é a mesma pergunta vista
     do outro lado: o `watchUser` acima diz o que o BANCO sabe, e isto
     confere se a LOJA sabe de algo que o banco não sabe. Os dois casos
     são reais e nenhum deles a pessoa consegue resolver sozinha — o
     aparelho novo, onde a compra está na conta Google e não neste banco,
     e a compra órfã, onde o pagamento passou e o app fechou antes de
     confirmar.

     Fora do TWA isto devolve na hora sem tocar a rede, porque a Digital
     Goods API nem existe lá. */
  useEffect(() => {
    if (!uid) return;
    void restaurarCompras();
  }, [uid]);

  const valor: Estado = {
    config,
    plano,
    assinante: plano === "assinante",
    tetoDeCaloriaHoje: tetoDeCaloria(config, plano),
    maxDeDesafios: limiteDeDesafios(config, plano),
    perfil,
  };

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export const usePlano = (): Estado => useContext(Contexto);

/**
 * Eu, com o nome e a foto que valem.
 *
 * A sessão (`user.displayName`/`user.photoURL`) guarda os dados do
 * LOGIN — trocar apelido ou foto em Conta não os alcança. O que vale
 * está em `users/{uid}`, e enquanto ele não chega vale o da sessão,
 * que não é pior que o de antes.
 *
 * Para GRAVAR nome e foto junto de um documento (prato, comentário,
 * ficha) isto é só o otimismo da tela: quem confere de novo é o
 * `perfilQueVale` do `@nhomi/dados`, então uma tela que esquecer não
 * espalha o dado velho.
 */
export function useMe(): { uid: string; name: string; photo: string } | null {
  const { user } = useSession();
  const { perfil } = usePlano();
  if (!user) return null;
  return {
    uid: user.uid,
    name: perfil?.name?.trim() || nomeDaSessao(user),
    photo: perfil?.photo || user.photoURL || "",
  };
}
